import { createMongoAbility, ExtractSubjectType, MongoQuery, SubjectRawRule } from '@casl/ability';
import { PackRule, unpackRules } from '@casl/ability/extra';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createContext, ReactNode, useMemo } from 'react';

import { Action, CaslRulesDto, Organization, Process, Subject, User } from '@/modules/api';
import { backendApiCaslGetRulesOptions } from '@/modules/api/@tanstack/react-query.gen';

export type SubjectEntity = Organization | User | Process;

export type AppAbilityTuple = [Action, Subject | SubjectEntity];

const getAbilityContextDefault = () =>
  createMongoAbility<AppAbilityTuple>([], { anyAction: Action.MANAGE, anySubjectType: Subject.All });

export const AbilityContext = createContext(getAbilityContextDefault());

type AbilityProviderProps = { children: ReactNode };

export function AbilityProvider({ children }: AbilityProviderProps) {
  return (
    <AbilityContext.Provider value={useMemo(() => getAbilityContextDefault(), [])}>{children}</AbilityContext.Provider>
  );
}

function createAbility(rules: CaslRulesDto['rules']) {
  return createMongoAbility<AppAbilityTuple>(
    unpackRules(rules as PackRule<SubjectRawRule<Action, ExtractSubjectType<Subject | SubjectEntity>, MongoQuery>>[]),
    {
      anyAction: Action.MANAGE,
      anySubjectType: Subject.All,
    },
  );
}

export const useAppAbility = () => {
  const { data: caslRules } = useSuspenseQuery(backendApiCaslGetRulesOptions());
  const ability = createAbility(caslRules.rules);

  return { ability };
};
